import axiosInstance from '../utils/axios';

/**
 * 팝업 관련 API 서비스
 */
class PopupService {
  /**
   * 팝업 목록을 조회합니다.
   * 
   * @returns {Promise<Object>} 팝업 목록
   */
  async getPopupList() {
    try {
      const response = await axiosInstance.get('/popup/list');

      if (response.data && response.data.data) {
        return {
          success: true,
          data: response.data.data
        };
      }
      
      return {
        success: false,
        message: response.data.message || '팝업 목록을 불러오는데 실패했습니다.'
      };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '팝업 목록을 불러오는 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * 팝업 상세 정보를 조회합니다.
   * 
   * @param {number} id 팝업 ID
   * @returns {Promise<Object>} 팝업 상세 정보
   */
  async getPopupDetail(id) {
    try {
      const response = await axiosInstance.get(`/popup/${id}`);
      
      if (response.data.success && response.data.data) {
        return {
          success: true,
          data: response.data.data
        };
      }
      
      return {
        success: false,
        message: response.data.message || '팝업 정보를 불러오는데 실패했습니다.'
      };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '팝업 정보를 불러오는 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * 새 팝업을 생성합니다.
   * 
   * @param {Object} popupData 팝업 데이터
   * @returns {Promise<Object>} 생성 결과
   */
  async createPopup(popupData) {
    try {

      const response = await axiosInstance.post('/popup', popupData);
      
      return {
        success: response.data.success,
        message: response.data.message || '팝업이 성공적으로 등록되었습니다.',
        data: response.data.data
      };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '팝업 등록 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * 기존 팝업을 수정합니다.
   * 
   * @param {number} id 팝업 ID
   * @param {Object} popupData 팝업 데이터
   * @returns {Promise<Object>} 수정 결과
   */
  async updatePopup(id, popupData) {
    try {
      const response = await axiosInstance.put(`/popup/${id}`, popupData);
      
      return {
        success: response.data.success,
        message: response.data.message || '팝업이 성공적으로 수정되었습니다.',
        data: response.data.data
      };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '팝업 수정 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * 팝업을 삭제합니다.
   * 
   * @param {number|number[]} ids 팝업 ID 또는 ID 배열
   * @returns {Promise<Object>} 삭제 결과
   */
  async deletePopups(ids) {
    try {
      // 단일 ID를 배열로 변환
      const idArray = Array.isArray(ids) ? ids : [ids];
      
      // 배치 엔드포인트 사용
      const response = await axiosInstance.delete('/popup/batch', {
        data: { ids: idArray }
      });
      
      return {
        success: response.data.success,
        message: response.data.message || '팝업이 성공적으로 삭제되었습니다.'
      };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '팝업 삭제 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * 팝업 사용 상태를 변경합니다.
   * 
   * @param {number} id 팝업 ID
   * @param {boolean} isActive 활성화 여부
   * @returns {Promise<Object>} 변경 결과
   */
  async changePopupState(id, isActive) {
    try {
      const response = await axiosInstance.patch(`/popup/${id}/state`, {
        state: isActive ? 'Y' : 'N'
      });
      
      return {
        success: response.data.success,
        message: response.data.message || '팝업 상태가 성공적으로 변경되었습니다.'
      };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '팝업 상태 변경 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * 팝업 순서를 변경합니다.
   * 
   * @param {Array<Object>} popupSequenceData 팝업 순서 데이터 배열 [{idx: number, sequence: number}]
   * @returns {Promise<Object>} 변경 결과
   */
  async changePopupSequence(popupSequenceData) {
    try {
      const response = await axiosInstance.patch('/popup/sequence', {
        sequence_data: popupSequenceData
      });
      
      return {
        success: response.data.success,
        message: response.data.message || '팝업 순서가 성공적으로 변경되었습니다.'
      };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '팝업 순서 변경 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  }
}

export default new PopupService();
